// Pure helpers that turn the /insights/summary payload into what the
// dashboard draws. Bucket strings arrive already in the viewer's time zone
// as 'YYYY-MM-DD HH:00', so nothing here does time-zone maths.

export const SYNC_FRESH_SECONDS = 30 * 60; // synced in the last 30 min
export const SYNC_STALE_SECONDS = 3 * 60 * 60; // nothing for 3 h+

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const numberFormat = new Intl.NumberFormat('en-IN');

export function formatNumber(value) {
  return numberFormat.format(Number(value) || 0);
}

/** 'YYYY-MM-DD' -> Date at local midnight (calendar maths only). */
function dateFromKey(key) {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function keyFromDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function shortDayLabel(key) {
  const date = dateFromKey(key);
  return `${WEEKDAYS[date.getDay()]} ${date.getDate()} ${MONTHS[date.getMonth()]}`;
}

function hourLabel(hour) {
  return `${String(hour).padStart(2, '0')}:00`;
}

export function parseNowLocal(nowLocal) {
  const [dayKey, time = '00:00'] = String(nowLocal || '').split(' ');
  const hour = Number(time.split(':')[0]) || 0;
  return { dayKey, hour };
}

/** Today's 24 hourly bars; hours after now are marked `future`. */
export function buildHourlyToday(activity, nowLocal) {
  const { dayKey, hour: currentHour } = parseNowLocal(nowLocal);
  const byBucket = new Map(
    (activity || []).map((row) => [row.bucket, row]),
  );

  return Array.from({ length: 24 }, (_, hour) => {
    const row = byBucket.get(`${dayKey} ${hourLabel(hour)}`);
    return {
      key: `h${hour}`,
      label: hourLabel(hour),
      rangeLabel: `${hourLabel(hour)}–${hourLabel((hour + 1) % 24)}`,
      scans: row ? Number(row.scans) : 0,
      units: row ? Number(row.units) : 0,
      future: hour > currentHour,
      current: hour === currentHour,
    };
  });
}

/** Last 14 days (today last), summed per local day. */
export function buildDaily(activity, nowLocal, days = 14) {
  const { dayKey } = parseNowLocal(nowLocal);
  const totals = new Map();

  for (const row of activity || []) {
    const key = row.bucket.slice(0, 10);
    const entry = totals.get(key) || { scans: 0, units: 0 };
    entry.scans += Number(row.scans) || 0;
    entry.units += Number(row.units) || 0;
    totals.set(key, entry);
  }

  const today = dateFromKey(dayKey);
  return Array.from({ length: days }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() - (days - 1 - index));
    const key = keyFromDate(date);
    const entry = totals.get(key) || { scans: 0, units: 0 };
    return {
      key,
      label: `${date.getDate()} ${MONTHS[date.getMonth()]}`,
      rangeLabel: shortDayLabel(key),
      scans: entry.scans,
      units: entry.units,
      future: false,
      current: index === days - 1,
      currentLabel: 'Today',
    };
  });
}

/** Scans per hour for the last `hours` hours, oldest first (sparkline). */
export function buildSparkline(hourly, nowLocal, hours = 8) {
  const { dayKey, hour } = parseNowLocal(nowLocal);
  const byBucket = new Map((hourly || []).map((row) => [row.bucket, Number(row.scans) || 0]));
  const now = dateFromKey(dayKey);
  now.setHours(hour);

  return Array.from({ length: hours }, (_, index) => {
    const slot = new Date(now);
    slot.setHours(now.getHours() - (hours - 1 - index));
    const key = `${keyFromDate(slot)} ${hourLabel(slot.getHours())}`;
    return byBucket.get(key) || 0;
  });
}

export function syncState(secondsSinceSync) {
  const seconds = Number(secondsSinceSync);
  if (!Number.isFinite(seconds)) return 'stale';
  if (seconds <= SYNC_FRESH_SECONDS) return 'fresh';
  if (seconds <= SYNC_STALE_SECONDS) return 'late';
  return 'stale';
}

export const SYNC_LABELS = {
  fresh: 'Synced in the last 30 min',
  late: 'Last sync 30 min – 3 h ago',
  stale: 'No sync for over 3 h',
};

export function formatAgo(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  if (s < 90) return 'just now';
  const minutes = Math.round(s / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours < 24) return rest ? `${hours} h ${rest} min ago` : `${hours} h ago`;
  const daysAgo = Math.floor(hours / 24);
  return `${daysAgo} day${daysAgo === 1 ? '' : 's'} ago`;
}

export function formatRemaining(endIso, nowMs = Date.now()) {
  const ms = new Date(endIso).getTime() - nowMs;
  if (!Number.isFinite(ms) || ms <= 0) return 'Ending now';
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes} min left`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) {
    const rest = minutes % 60;
    return rest ? `${hours} h ${rest} min left` : `${hours} h left`;
  }
  return `${Math.floor(hours / 24)} days left`;
}

export function formatStart(startIso) {
  const date = new Date(startIso);
  if (Number.isNaN(date.getTime())) return { day: '—', time: '' };
  return {
    day: `${WEEKDAYS[date.getDay()]} ${date.getDate()} ${MONTHS[date.getMonth()]}`,
    time: `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`,
  };
}

export function auditPlace(audit) {
  return [audit.outlet_name, audit.organization_name].filter(Boolean).join(', ');
}

export function coverage(audit) {
  const total = Number(audit.product_count) || 0;
  const found = Math.min(Number(audit.products_found) || 0, total || Infinity);
  return {
    total,
    found,
    percent: total > 0 ? Math.round((found / total) * 100) : 0,
  };
}

/**
 * Things a manager should act on, most urgent first.
 * severity: 'critical' blocks the audit; 'warning' needs a look.
 */
export function buildAttention(summary, nowMs = Date.now()) {
  if (!summary) return [];
  const items = [];

  for (const audit of summary.live || []) {
    const name = audit.name;
    const portal = `/audits/${audit.id}/portal`;
    const startedMinutesAgo = (nowMs - new Date(audit.start_at).getTime()) / 60000;

    if (!Number(audit.product_count)) {
      items.push({
        id: `nomaster-${audit.id}`,
        severity: 'critical',
        title: `${name} has no product list`,
        detail: 'Phones can’t download products to scan against. Upload the master file.',
        to: '/audits',
        action: 'Upload product list',
      });
    }
    if (!Number(audit.team_count)) {
      items.push({
        id: `noteam-${audit.id}`,
        severity: 'critical',
        title: `${name} has no team`,
        detail: 'Nobody is assigned, so no phone will show this schedule.',
        to: '/audits',
        action: 'Assign a team',
      });
    }
    if (!Number(audit.scans_total) && startedMinutesAgo > 60) {
      items.push({
        id: `noscans-${audit.id}`,
        severity: 'warning',
        title: `No scans from ${name} yet`,
        detail: `Started ${formatAgo(startedMinutesAgo * 60)}. Phones may be out of signal; their scans upload when they reconnect.`,
        to: portal,
        action: 'Open portal',
      });
    }

    const silent = (audit.phones || []).filter(
      (phone) => syncState(phone.seconds_since_sync) === 'stale',
    );
    if (silent.length) {
      items.push({
        id: `silent-${audit.id}`,
        severity: 'warning',
        title:
          silent.length === 1
            ? `1 phone on ${name} hasn’t synced in over 3 h`
            : `${silent.length} phones on ${name} haven’t synced in over 3 h`,
        detail: 'Likely out of signal. Their scans are safe on the phone until they reconnect.',
        to: portal,
        action: 'Open portal',
      });
    }
  }

  for (const audit of summary.upcoming || []) {
    const hoursAway = (new Date(audit.start_at).getTime() - nowMs) / 3600000;
    if (hoursAway > 48) continue;
    const when = formatStart(audit.start_at);

    if (!audit.has_master_file) {
      items.push({
        id: `up-nomaster-${audit.id}`,
        severity: 'warning',
        title: `${audit.name} starts ${when.day} ${when.time} without a product list`,
        detail: 'Upload it now so teams can download it while they still have signal.',
        to: '/audits',
        action: 'Upload product list',
      });
    }
    if (!Number(audit.team_count)) {
      items.push({
        id: `up-noteam-${audit.id}`,
        severity: 'warning',
        title: `${audit.name} starts ${when.day} ${when.time} with no team`,
        detail: 'Assign a team before it starts.',
        to: '/audits',
        action: 'Assign a team',
      });
    }
  }

  const rank = { critical: 0, warning: 1 };
  return items.sort((a, b) => rank[a.severity] - rank[b.severity]);
}
